import { DemoPlayerContext } from '../../player-clock'
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react'

import { useRecalculateTestDriveScenarioMutation } from '../../../../../api/test_drive'
import { isDemoPage } from '../../../../../utils/hooks'
import { useDemoPackagingPlaybackComplete } from './use-packaging'
import {
  DEMO_FILL_ADDITIONAL_FIELDS_ACTION,
  DEMO_PRODUCT_COUNTRY_SEARCH_EVENT,
  DEMO_PRODUCT_READY_EVENT,
  DEMO_SAVE_ADDITIONAL_FIELDS_ACTION,
} from '../../playback-constants'
import DemoCalculatedFieldsLoader, {
  getDemoCalculatedFieldValues,
} from './components/calculated-fields-loader'
import { DemoRollingDirection } from './components/rolling-number'

const PRODUCT_FIELD = {
  CODE: 'code',
  PIECES: 'pieces',
  UNIT_PRICE: 'unit_price',
}

const PRODUCT_FIELDS_TO_PRESERVE = [
  'engine_type',
  'engine_power_hp',
  'engine_volume_cc',
  'manufacture_date',
  'excise_rate',
  'excise_value',
]

export const getDemoProductData = (
  productData: ProductParamsOutput,
  currentValues: DemoProductFormValues,
) => {
  if (!isDemoPage()) {
    return productData
  }

  const preservedFields = PRODUCT_FIELDS_TO_PRESERVE.reduce<Record<string, unknown>>(
    (fields, fieldName) => {
      if (currentValues[fieldName] !== undefined) {
        fields[fieldName] = currentValues[fieldName]
      }

      return fields
    },
    {},
  )

  return {
    ...productData,
    ...preservedFields,
  }
}

export const useDemoProductForm = ({
  containerRef,
  dirtyFields,
  getValues,
  onError,
  onSuccess,
  product,
  scenarioSlug,
}: UseDemoProductFormParams) => {
  const player = useContext(DemoPlayerContext)
  const isDemoMode = isDemoPage()
  const isPlaybackComplete = useDemoPackagingPlaybackComplete()
  const [ recalculate, { isLoading: isRecalculating } ] = useRecalculateTestDriveScenarioMutation()
  const [ rollingDirection, setRollingDirection ] = useState<DemoRollingDirection>('up')
  const [ rollingValues, setRollingValues ] = useState<Record<string, unknown>>({})
  const [ saveButtonIsReady, setSaveButtonIsReady ] = useState(false)

  useEffect(() => {
    if (!isDemoMode) {
      return
    }

    const handleReady = () => setSaveButtonIsReady(true)

    window.addEventListener(DEMO_PRODUCT_READY_EVENT, handleReady)

    return () => window.removeEventListener(DEMO_PRODUCT_READY_EVENT, handleReady)
  }, [ isDemoMode ])

  const getRollingDirection = (values: DemoProductFormValues): DemoRollingDirection => {
    const piecesChanged = Boolean(dirtyFields[PRODUCT_FIELD.PIECES])
    const previousValue = piecesChanged ? product?.pieces : product?.unit_price
    const nextValue = piecesChanged ? values[PRODUCT_FIELD.PIECES] : values[PRODUCT_FIELD.UNIT_PRICE]

    return Number(nextValue) >= Number(previousValue) ? 'up' : 'down'
  }

  const captureRollingState = (values: DemoProductFormValues) => {
    setRollingDirection(getRollingDirection(values))
    setRollingValues(getDemoCalculatedFieldValues(containerRef.current))
  }

  useLayoutEffect(() => {
    if (!isRecalculating) {
      return
    }

    captureRollingState(getValues())
  }, [ isRecalculating ])

  const handleSend = async (values: DemoProductFormValues) => {
    if (!isDemoMode) {
      return false
    }

    if (player) {
      onSuccess()
      return true
    }

    if (!scenarioSlug) {
      return true
    }

    captureRollingState(values)

    const result = await recalculate({
      items: [
        {
          id: `item-${scenarioSlug}`,
          quantity: Number(values[PRODUCT_FIELD.PIECES]),
          unit_price: Number(values[PRODUCT_FIELD.UNIT_PRICE]).toFixed(2),
        },
      ],
      slug: scenarioSlug,
    })
    const errorData = (result as DemoRecalculateMutationError).error?.data

    if (errorData) {
      onError(errorData.non_field_errors?.[0] || true)
      return true
    }

    onError(false)
    onSuccess()

    return true
  }

  const excludedFieldNames = [
    PRODUCT_FIELD.CODE,
    ...Object.keys(dirtyFields),
  ]

  return {
    canChangeManually: !isDemoMode,
    fieldDisabled: isDemoMode && isPlaybackComplete,
    handleSend,
    isDemoMode,
    isRecalculating,
    loader: (
      <DemoCalculatedFieldsLoader
        active={isRecalculating}
        containerRef={containerRef}
        direction={rollingDirection}
        excludedFieldNames={excludedFieldNames}
        fieldValues={rollingValues}
      />
    ),
    saveButtonDisabled: isDemoMode && !saveButtonIsReady,
    saveButtonProps: {
      'data-demo-allowed': isDemoMode ? 'save-product' : undefined,
      'data-demo-playback-action': isDemoMode ? 'save-product' : undefined,
    },
  }
}

export const useDemoCommonProductFields = (isRecalculating: boolean) => {
  const player = useContext(DemoPlayerContext)
  const isDemoMode = isDemoPage()
  const isPlaybackComplete = useDemoPackagingPlaybackComplete()

  return {
    disabled: isDemoMode && isPlaybackComplete,
    getEditableInputProps: (fieldName: string) => ({
      'aria-busy': isRecalculating,
      'data-demo-editable': player ? undefined : fieldName,
      readOnly: isRecalculating || Boolean(player),
    }),
    isDemoMode,
  }
}

export const useDemoProductCountry = ({
  onSearch,
  onSearchStart,
}: UseDemoProductCountryParams) => {
  const isDemoMode = isDemoPage()

  useEffect(() => {
    if (!isDemoMode) {
      return
    }

    const handleSearch = (event: Event) => {
      const query = (event as CustomEvent<{ query: string }>).detail?.query || ''

      if (query.length < 3) {
        return
      }

      onSearchStart()
      onSearch(query)
    }

    window.addEventListener(DEMO_PRODUCT_COUNTRY_SEARCH_EVENT, handleSearch)

    return () => window.removeEventListener(DEMO_PRODUCT_COUNTRY_SEARCH_EVENT, handleSearch)
  }, [ isDemoMode, onSearch, onSearchStart ])
}

export const useDemoExcise = () => {
  const player = useContext(DemoPlayerContext)
  const isDemoMode = isDemoPage()
  const useDemoModalWidth = isDemoMode && Boolean(player)

  return {
    fillButtonProps: {
      'data-demo-allowed': isDemoMode ? DEMO_FILL_ADDITIONAL_FIELDS_ACTION : undefined,
      'data-demo-playback-action': isDemoMode ? DEMO_FILL_ADDITIONAL_FIELDS_ACTION : undefined,
    },
    modalWidth: useDemoModalWidth ? 1410 : undefined,
  }
}

export const useDemoExciseFields = (prefillSuggestions?: SuggestAdditionalFieldsOutput | null) => {
  const isDemoMode = isDemoPage()

  return {
    additionalFieldsProps: {
      'data-demo-excise-rate-index': isDemoMode
        ? prefillSuggestions?.excise_rate_index ?? undefined
        : undefined,
      'data-demo-engine-power-hp': isDemoMode
        ? prefillSuggestions?.engine_power_hp ?? undefined
        : undefined,
      'data-demo-engine-type': isDemoMode
        ? prefillSuggestions?.engine_type ?? undefined
        : undefined,
      'data-demo-engine-volume-cc': isDemoMode
        ? prefillSuggestions?.engine_volume_cc ?? undefined
        : undefined,
      'data-demo-manufacture-date': isDemoMode
        ? prefillSuggestions?.manufacture_date ?? undefined
        : undefined,
    },
    getProductFormValues: (values: Record<string, unknown>) => isDemoMode ? values : {},
    radioGroupProps: {
      'data-demo-playback-group': isDemoMode ? 'additional-fields-radio' : undefined,
    },
    getRadioProps: (index: number) => ({
      'data-demo-playback-radio': isDemoMode ? index : undefined,
    }),
    saveButtonProps: {
      'data-demo-allowed': isDemoMode ? DEMO_SAVE_ADDITIONAL_FIELDS_ACTION : undefined,
      'data-demo-playback-action': isDemoMode ? DEMO_SAVE_ADDITIONAL_FIELDS_ACTION : undefined,
    },
    shouldApplyExciseRateIndex: !isDemoMode,
  }
}
